// Winners/Hall of Fame service - League-level winners
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

// Create a new winner record for a league
export const createWinnerRecord = async (leagueId, winnerData) => {
  const winnerId = uuidv4();
  const winnerRef = doc(db, 'winners', winnerId);

  const winner = {
    leagueId,
    competitionId: winnerData.competitionId || null,
    competitionName: winnerData.competitionName,
    year: winnerData.year,
    winnerName: winnerData.winnerName,
    categoryName: winnerData.categoryName || null, // For category winners
    winnerType: winnerData.winnerType || 'overall', // 'overall' or 'category'
    photoUrl: winnerData.photoUrl || null,
    notes: winnerData.notes || null,
    isAutomatic: winnerData.isAutomatic || false, // True when auto-added from competition results
    playerId: winnerData.playerId || null, // Link to player record if available
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(winnerRef, winner);

  return {
    id: winnerId,
    ...winner
  };
};

// Get all winners for a league
export const getWinnersForLeague = async (leagueId) => {
  const winnersRef = collection(db, 'winners');
  const q = query(
    winnersRef,
    where('leagueId', '==', leagueId)
  );
  const querySnapshot = await getDocs(q);

  const winners = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Sort by year descending, then by competition name
  return winners.sort((a, b) => {
    if (b.year !== a.year) return (b.year || 0) - (a.year || 0);
    return (a.competitionName || '').localeCompare(b.competitionName || '');
  });
};

// Get all winners for a competition (backwards compatible)
export const getWinnersForCompetition = async (competitionId) => {
  const winnersRef = collection(db, 'winners');
  const q = query(
    winnersRef,
    where('competitionId', '==', competitionId)
  );
  const querySnapshot = await getDocs(q);

  const winners = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Sort by year descending in JavaScript
  return winners.sort((a, b) => (b.year || 0) - (a.year || 0));
};

// Get a single winner record
export const getWinnerRecord = async (winnerId) => {
  const winnerRef = doc(db, 'winners', winnerId);
  const winnerSnap = await getDoc(winnerRef);

  if (!winnerSnap.exists()) {
    return null;
  }

  return {
    id: winnerSnap.id,
    ...winnerSnap.data()
  };
};

// Update winner record
export const updateWinnerRecord = async (winnerId, updates) => {
  const winnerRef = doc(db, 'winners', winnerId);
  await updateDoc(winnerRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Delete winner record
export const deleteWinnerRecord = async (winnerId) => {
  const winnerRef = doc(db, 'winners', winnerId);
  await deleteDoc(winnerRef);
};

// Add competition winner to league hall of fame
// Called when a competition is completed to record the winner
export const addCompetitionWinnerToLeague = async (leagueId, competitionId, competitionName, winnerName, playerId = null, year = null, photoUrl = null) => {
  // Check if this winner already exists for this competition
  const existingWinners = await getWinnersForCompetition(competitionId);
  const alreadyExists = existingWinners.some(
    w => w.winnerType === 'overall' && w.winnerName === winnerName
  );

  if (alreadyExists) {
    console.log('Winner already exists in hall of fame for this competition');
    return null;
  }

  return createWinnerRecord(leagueId, {
    competitionId,
    competitionName,
    year: year || new Date().getFullYear(),
    winnerName,
    winnerType: 'overall',
    playerId,
    photoUrl,
    isAutomatic: true
  });
};

// Add category winner to league hall of fame
export const addCategoryWinnerToLeague = async (leagueId, competitionId, competitionName, categoryName, winnerName, playerId = null, year = null) => {
  // Check if this category winner already exists
  const existingWinners = await getWinnersForCompetition(competitionId);
  const alreadyExists = existingWinners.some(
    w => w.winnerType === 'category' && w.categoryName === categoryName && w.winnerName === winnerName
  );

  if (alreadyExists) {
    console.log('Category winner already exists in hall of fame');
    return null;
  }

  return createWinnerRecord(leagueId, {
    competitionId,
    competitionName,
    year: year || new Date().getFullYear(),
    winnerName,
    categoryName,
    winnerType: 'category',
    playerId,
    isAutomatic: true
  });
};
